// Workspace (DESIGN §3 + §7) — "What are we working on?" The primary page.
// /workspace?filter=all|mine|review|done&client=<id>&q=
// Open batches of the Active clients by due date. The Client dropdown is a filter over all of that client's months,
// with "Open client →" to its workspace (/clients/:id — client.jsx). Legacy ?client=&month= links redirect there.
import './workspace.css';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { setCurrentPerson, setWorkspaceFilterPref, useStore } from '../store.js';
import { batchHref, clientHref, setQuery } from '../router.js';
import { Page } from '../components/page.jsx';
import { BatchTable } from '../components/batch-row.jsx';
import { FilterButtons } from '../components/filter-buttons.jsx';
import { CompactDropdown } from '../components/compact-dropdown.jsx';
import { Button, EmptyState } from '../components/ui.jsx';
import { Icon } from '../icons.jsx';
import { openAddBatch, openAddClient } from '../components/forms.jsx';
import { monthLabel } from '../../shared/constants.js';
import { activeMonthNumber, workspaceList, WORKSPACE_FILTERS } from '../../shared/derive.js';
import { formatDate } from '../../shared/dates.js';

const PAGE_SIZE = 50;
const FILTER_LABELS = { all: 'All', mine: 'My Work', review: 'Review', done: 'Done' };
const STATUS_RANK = { Active: 0, Paused: 1, Completed: 2 };

// Active first, then Paused, then Completed; alphabetical within each group.
export function compareClients(a, b) {
  return (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9)
    || a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
}

// "Active · Month 04" | "Paused · Month 11" | "Starts Mar 1, 2027" (year only when not this year — QA-21).
export function clientMeta(client, today) {
  if (client.startDate && today < client.startDate) {
    const sameYear = client.startDate.slice(0, 4) === String(today).slice(0, 4);
    return `Starts ${formatDate(client.startDate, sameYear ? 'short' : 'long')}`;
  }
  return `${client.status} · ${monthLabel(activeMonthNumber(client, today))}`;
}

// Search field: typing updates `q` in the URL after a short pause; Esc clears.
function SearchField({ value }) {
  const [text, setText] = useState(value);
  const timer = useRef(0);
  useEffect(() => { if (!timer.current) setText(value); }, [value]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const commit = (v) => {
    clearTimeout(timer.current);
    timer.current = 0;
    setQuery({ q: v.trim() });
  };
  const onInput = (e) => {
    const v = e.currentTarget.value;
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(v), 250);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape' && text) { e.preventDefault(); setText(''); commit(''); }
    if (e.key === 'Enter') commit(text);
  };
  return (
    <label class="input-icon ws-search">
      <Icon name="search" size={15} />
      <input class="input" type="search" value={text} placeholder="Search batches" aria-label="Search batches" onInput={onInput} onKeyDown={onKeyDown} maxLength={120} />
    </label>
  );
}

function ClientFilter({ state, clientId, today }) {
  const clients = useMemo(() => [...state.clients].sort(compareClients), [state.clients]);
  const options = [{ value: '', label: 'All clients' }, ...clients.map((c) => ({ value: c.id, label: c.name, meta: clientMeta(c, today) }))];
  const current = clients.find((c) => c.id === clientId);
  return (
    <CompactDropdown
      label="Client:"
      value={clientId}
      valueLabel={current ? current.name : 'All'}
      options={options}
      width={300}
      class="ws-client-filter"
      onChange={(id) => setQuery({ client: id })}
      extra={[{ label: 'New client', icon: 'plus', onSelect: () => openAddClient() }]}
    />
  );
}

// My Work without a chosen person: pick one right here (one click) instead of hunting for the switcher.
function ChoosePerson({ team }) {
  return (
    <EmptyState
      compact
      title="Choose your name to see your work."
      action={(
        <CompactDropdown
          variant="bordered"
          placeholder="Choose your name"
          ariaLabel="Choose your name"
          options={team.map((m) => ({ value: m.id, label: m.name, meta: m.role }))}
          onChange={(id) => setCurrentPerson(id)}
        />
      )}
    />
  );
}

// ---------- view ----------

export function WorkspaceView({ route }) {
  const { state, index, today, currentPerson } = useStore();
  const { query } = route;
  const filter = WORKSPACE_FILTERS.includes(query.filter) ? query.filter : 'all';
  const q = String(query.q || '').trim();
  const clientId = route.params.clientId || '';
  const client = clientId ? index.clientsById.get(clientId) : null;
  const [limit, setLimit] = useState(PAGE_SIZE);
  useEffect(() => setLimit(PAGE_SIZE), [filter, q, clientId]);

  // The Client dropdown is a filter (all of that client's months); its workspace lives at /clients/:id.
  const list = useMemo(
    () => workspaceList(state, index, { filter, personId: currentPerson, clientId: client ? client.id : '', query: q, today }),
    [state, index, filter, currentPerson, client, q, today],
  );
  const batches = useMemo(() => list.rows.map((r) => r.batch), [list]);

  const addBatch = () => openAddBatch(client ? { clientId: client.id } : {}, { onCreated: focusCreated });
  // No clients yet: nothing to search or filter — one empty state whose button adds the first client.
  if (!state.clients.length) {
    return (
      <Page title="Workspace" class="ws-page">
        <EmptyState title="No clients yet." description="Every batch belongs to a client. Add your first client to start production."
          action={<Button variant="primary" icon="plus" onClick={() => openAddClient()}>Add Client</Button>} />
      </Page>
    );
  }
  const actions = (
    <>
      <SearchField value={q} />
      <Button variant="primary" icon="plus" onClick={addBatch}>Add Batch</Button>
    </>
  );

  const options = WORKSPACE_FILTERS.map((k) => ({ value: k, label: FILTER_LABELS[k], count: list.counts[k] }));
  const empty = filter === 'mine' && !currentPerson
    ? <ChoosePerson team={state.team} />
    : <WorkspaceEmpty filter={filter} q={q} />;

  return (
    <Page title="Workspace" actions={actions} class="ws-page">
      <div class="toolbar ws-toolbar">
        <FilterButtons options={options} value={filter} label="Show"
          onChange={(v) => { setWorkspaceFilterPref(v); setQuery({ filter: v === 'all' ? '' : v }); }} />
        <ClientFilter state={state} clientId={client ? client.id : ''} today={today} />
        {client && <a class="link-quiet ws-open-client" href={clientHref(client.id)}>Open client →</a>}
      </div>
      <BatchTable
        batches={batches}
        index={index}
        today={today}
        showClient={!client}
        // My Work also lists batches whose current stage waits on me as the client's PIC (DESIGN §9): the OWNER
        // column comes back as soon as one of them is owned by someone else.
        showOwner={filter !== 'mine' || batches.some((b) => b.owner !== currentPerson)}
        empty={empty}
        limit={limit}
        onMore={() => setLimit((n) => n + PAGE_SIZE)}
        label={client ? `${client.name} batches` : 'Batches'}
        class="ws-table"
      />
    </Page>
  );
}

// ---------- parts ----------

// After Add Batch the empty state that held the focused button may be gone (A11Y-05): move focus to the new row,
// or back to the header's Add Batch when the row is not listed, instead of leaving it on <body>.
function focusCreated(result) {
  setTimeout(() => {
    const active = document.activeElement;
    if (active && active !== document.body && document.contains(active)) return;
    const row = result?.batchId && document.querySelector(`.ws-page a[href="${batchHref(result.batchId)}"]`);
    (row || document.querySelector('.ws-page .page-actions .btn-primary'))?.focus();
  }, 80);
}

const EMPTY_TEXT = { all: 'No open batches.', mine: 'Nothing assigned to you.', review: 'Nothing waiting for review.', done: 'No completed batches yet.' };

function WorkspaceEmpty({ filter, q }) {
  if (q) return <EmptyState compact title={`No batches match “${q}”.`} action={<Button size="sm" onClick={() => setQuery({ q: '' })}>Clear search</Button>} />;
  return <EmptyState compact title={EMPTY_TEXT[filter] || EMPTY_TEXT.all} />;
}
